import { useState } from 'react'
import { Head, router, usePage } from '@inertiajs/react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/Card'
import { Wand2 } from 'lucide-react'
import appIcon from '@/assets/images/icon-128.png'
import sittingGhost from '@/assets/images/sitting-ghost.png'
import { GoogleIcon, MicrosoftIcon } from '@/components/icons'
import { AuthBackground } from '@/components/auth/AuthBackground'

interface LoginProps {
  errors?: Record<string, string>
}

export default function Login({ errors }: LoginProps) {
  const { flash } = usePage<{ flash: { notice?: string; alert?: string } }>().props
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  })

  const submitProvider = (provider: 'google_oauth2' | 'microsoft_graph') => {
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = `/auth/${provider}`

    if (csrfToken) {
      const tokenInput = document.createElement('input')
      tokenInput.type = 'hidden'
      tokenInput.name = 'authenticity_token'
      tokenInput.value = csrfToken
      form.appendChild(tokenInput)
    }

    document.body.appendChild(form)
    form.submit()
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    router.post('/login', { user: formData }, {
      onFinish: () => setIsSubmitting(false),
    })
  }

  return (
    <>
      <Head title="Login" />

      <AuthBackground>
        <div className="relative w-full max-w-md">
          <img
            src={sittingGhost}
            alt=""
            className="absolute -top-18 right-0 size-32 z-10 pointer-events-none"
          />
          <Card className="w-full">
          <CardHeader className="text-center">
            <img src={appIcon} alt="Banshee" className="size-16 mx-auto mb-2 rounded-xl" />
            <CardTitle>Welcome back</CardTitle>
            <CardDescription>Sign in to your Banshee account</CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {flash?.notice && (
              <div className="p-3 rounded-lg bg-primary/10 text-sm text-primary">
                {flash.notice}
              </div>
            )}
            {flash?.alert && (
              <div className="p-3 rounded-lg bg-destructive/10 text-sm text-destructive">
                {flash.alert}
              </div>
            )}

            {/* Email/Password Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="ghost@example.com"
                  required
                />
                {errors?.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <a href="/password/new" className="text-xs text-muted-foreground hover:text-foreground cursor-pointer">
                    Forgot password?
                  </a>
                </div>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
                {errors?.password && (
                  <p className="text-sm text-destructive">{errors.password}</p>
                )}
              </div>

              <Button type="submit" className="w-full cursor-pointer" disabled={isSubmitting}>
                {isSubmitting ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Or</span>
              </div>
            </div>

            {/* Alternative Sign-in Options */}
            <div className="space-y-3">
              <Button
                variant="outline"
                type="button"
                className="w-full cursor-pointer"
                onClick={() => submitProvider('google_oauth2')}
              >
                <GoogleIcon className="size-5" />
                Continue with Google
              </Button>
              <Button
                variant="outline"
                type="button"
                className="w-full cursor-pointer"
                onClick={() => submitProvider('microsoft_graph')}
              >
                <MicrosoftIcon className="size-5" />
                Continue with Microsoft
              </Button>
              <Button
                variant="outline"
                className="w-full cursor-pointer"
                onClick={() => window.location.href = '/login/magic'}
              >
                <Wand2 className="size-4" />
                Sign in with magic link
              </Button>
            </div>
          </CardContent>

          <CardFooter className="justify-center">
            <p className="text-sm text-muted-foreground">
              New to Banshee.chat?{' '}
              <a href="/early-access" className="text-primary hover:underline cursor-pointer">Start your journey</a>
            </p>
          </CardFooter>
          </Card>
        </div>
      </AuthBackground>
    </>
  )
}
